import { useRef, useState } from "react";
import heic2any from "heic2any";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import axiosInstance, { setAxiosToken } from "../../../../common/infrastructure/helpers/axiosHelper";
import { useAuthentication } from "../../../../common/infrastructure/hooks/useAuthentication";

interface IUploadedAttachment {
  id: number;
  fileName: string;
  url: string;
  fileSize: number;
}

interface IUseSupportChatInputProps {
  onSend: (content: string, attachmentIds: number[]) => void;
}

const isTouchScreen = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;

export const useSupportChatInput = ({ onSend }: IUseSupportChatInputProps) => {
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<IUploadedAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { currentUser } = useAuthentication();

  const handleSend = () => {
    if (!content.trim() && attachments.length === 0) return;

    const contentToSend = content.trim();
    const attachmentIdsToSend = attachments.map((a) => a.id);

    setContent("");
    setAttachments([]);

    onSend(contentToSend, attachmentIdsToSend);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    // Phone keyboards have no Shift+Enter, so there Enter starts a new line and the button sends.
    if (isTouchScreen()) return;

    e.preventDefault();
    handleSend();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      setAxiosToken(currentUser?.authToken);

      let uploadFile: File = file;
      const isHeic = file.type === "image/heic" || file.type === "image/heif"
        || file.name.toLowerCase().endsWith(".heic") || file.name.toLowerCase().endsWith(".heif");

      if (isHeic) {
        try {
          const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
          const blob = Array.isArray(converted) ? converted[0] : converted;
          const jpegName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
          uploadFile = new File([blob], jpegName, { type: "image/jpeg" });
        } catch {
          // Conversion failed — upload original; server will validate content type
        }
      }

      const formData = new FormData();
      formData.append("file", uploadFile);

      const response = await axiosInstance.post(
        endpointsMetadata.supportChat.uploadAttachment,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;
      setAttachments((prev) => [
        ...prev,
        {
          id: data.id ?? data.Id,
          fileName: data.fileName ?? data.FileName ?? file.name,
          url: data.url ?? data.Url ?? "",
          fileSize: data.fileSize ?? data.FileSize ?? file.size,
        },
      ]);
    } catch {
      // error handled by axios interceptor
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id: number) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  return {
    content,
    setContent,
    attachments,
    isUploading,
    fileInputRef,
    textareaRef,
    handleSend,
    handleKeyDown,
    handleFileChange,
    removeAttachment,
  };
};
